<script setup>
import DateRangePicker from '@js/components/pickers/DateRangePicker.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PercentageCell from '@js/components/tables/cells/PercentageCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import StatusCell from '@js/components/tables/cells/StatusCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'type',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Type',
                }),
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.type.label,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'isActive',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actif',
                }),
            cell: (props) =>
                h(StatusCell, {
                    status: props.row.original.isActive,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'emails',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nb emails<br/>envoyés',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.emails,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'opened',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Ouvertures',
                }),
            cell: (props) =>
                h(PercentageCell, {
                    percentage: props.row.original.opened,
                    withDecimals: true,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'clicked',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Clics',
                }),
            cell: (props) =>
                h(PercentageCell, {
                    percentage: props.row.original.clicked,
                    withDecimals: true,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'bounce',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Rebonds',
                }),
            cell: (props) =>
                h(PercentageCell, {
                    percentage: props.row.original.bounce,
                    withDecimals: true,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'spam',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Spams',
                }),
            cell: (props) =>
                h(PercentageCell, {
                    percentage: props.row.original.spam,
                    withDecimals: true,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'blocked',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Bloqués',
                }),
            cell: (props) =>
                h(PercentageCell, {
                    percentage: props.row.original.blocked,
                    withDecimals: true,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'unsub',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Désabonnements',
                }),
            cell: (props) =>
                h(PercentageCell, {
                    percentage: props.row.original.unsub,
                    withDecimals: true,
                }),
        }),
    ],
    url: route(
        'front.projectManagement.modules.postEmailingAutomationDeliverability',
        {
            tenant: tenant.id,
        },
    ),
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    >
        <template #actions>
            <DateRangePicker :callback="updateData" can-select-today />
        </template>
    </DataTableLayout>
</template>
